import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Breadcrumbs, Skeleton, SxProps, Theme, Typography } from "@mui/material";
import { useCallback, useRef } from "react";
import { isMobile } from "react-device-detect";
import { useLocation } from "react-router-dom";

import {
  useBreadcrumbsContext,
  useBreadcrumbsEventEmitterSubscription,
} from "../contexts/breadcrumbs";
import { BreadcrumbHelper } from "../helpers/breadcrumb";
import { ComparisonHelper } from "../helpers/comparison";
import { useEffectWithDeepCompare } from "../hooks/effect/useEffectWithDeepCompare";
import { useTriggerRender } from "../hooks/render/useTriggerRender";
import {
  AppBreadcrumbsReplacements,
  IAppBreadcrumb,
  IAppBreadcrumbIdValueReplacement,
} from "../ts/breadcrumbs";
import DelayedRender from "./DelayedRender";
import AppLink from "./Link/AppLink";

const maxCacheKeys = 100;

const defaultReplacements: AppBreadcrumbsReplacements = {
  waitTimeout: 1000,
  isWaitInfinitely: false,
};

export type AppBreadcrumbsReplacementsMap = Record<
  string,
  IAppBreadcrumbIdValueReplacement | undefined
>;

export interface AppBreadcrumbsProps {
  /** Override breadcrumbs resolved from current path. */
  breadcrumbs?: IAppBreadcrumb[];
  replacements?: AppBreadcrumbsReplacements;
  /** Always show Home as first breadcrumb. */
  withHome?: boolean;
  sx?: SxProps<Theme>;
}

export default function AppBreadcrumbs({ breadcrumbs, replacements, sx }: AppBreadcrumbsProps) {
  const location = useLocation();
  const breadcrumbsContext = useBreadcrumbsContext();

  // use imperative style render call to be able to use refs in event handlers
  const { triggerRender } = useTriggerRender();

  const breadcrumbsComputedRef = useRef<IAppBreadcrumb[]>([]);
  const replacementsComputedRef = useRef<AppBreadcrumbsReplacements>({ ...defaultReplacements });
  // {id: value}
  const idValueReplacementsMapRef = useRef({} as AppBreadcrumbsReplacementsMap);

  const applyIdValueReplacements = useCallback(
    (
      currentBreadCrumbs: IAppBreadcrumb[],
      idValueReplacementsMap: AppBreadcrumbsReplacementsMap,
    ) => {
      const newBreadcrumbsComputed = currentBreadCrumbs.map((b) => {
        const idValueReplacement = idValueReplacementsMap[b.idValue || ""];
        return {
          ...b,
          title: (b.idValue && idValueReplacement?.newTitle) || b.title,
          isReplacementApplied: !!idValueReplacement,
        };
      });

      if (!ComparisonHelper.isDeepEqual(newBreadcrumbsComputed, currentBreadCrumbs)) {
        breadcrumbsComputedRef.current = newBreadcrumbsComputed;
        triggerRender();
      }

      // keep local cache small
      const keys = Object.keys(idValueReplacementsMap);
      if (keys.length >= maxCacheKeys) {
        const diff = keys.length - maxCacheKeys;
        const keysToDeleteCount = Math.max(keys.length - diff - maxCacheKeys / 2, 0);
        let i = 0;
        while (i < keysToDeleteCount) {
          delete idValueReplacementsMap[keys[i]];
          i += 1;
        }
      }
    },
    [triggerRender],
  );

  useEffectWithDeepCompare(() => {
    // combine replacements
    const newReplacementsComputed = BreadcrumbHelper.combineReplacements(
      replacementsComputedRef.current,
      replacements,
    );
    const isReplacementsChanged = !ComparisonHelper.isDeepEqual(
      newReplacementsComputed,
      replacementsComputedRef.current,
    );

    if (isReplacementsChanged) {
      replacementsComputedRef.current = newReplacementsComputed;
      idValueReplacementsMapRef.current = BreadcrumbHelper.combineReplacementsMap(
        idValueReplacementsMapRef.current,
        replacements,
      );
    }

    // use new breadcrumbs
    const newValue =
      breadcrumbs || BreadcrumbHelper.getByPath(location.pathname, { includeHome: true });
    const isBreadcrumbsChanged = !ComparisonHelper.isDeepEqual(
      newValue,
      breadcrumbsComputedRef.current,
    );
    if (isBreadcrumbsChanged) {
      breadcrumbsComputedRef.current = newValue;
      triggerRender();
    }

    if (isReplacementsChanged || isBreadcrumbsChanged) {
      applyIdValueReplacements(newValue, idValueReplacementsMapRef.current);
    }
  }, [breadcrumbs, replacements, location.pathname]);

  // listen for replacements from actual pages
  useBreadcrumbsEventEmitterSubscription(
    breadcrumbsContext,
    "idBreadcrumbReplacementReceived",
    (data) => {
      const newValue = data;
      const currentValue = idValueReplacementsMapRef.current[data.idValue];

      if (!ComparisonHelper.isDeepEqual(newValue, currentValue)) {
        const key = data.idValue;
        idValueReplacementsMapRef.current[key] = data;

        applyIdValueReplacements(breadcrumbsComputedRef.current, idValueReplacementsMapRef.current);
      }
    },
  );

  useBreadcrumbsEventEmitterSubscription(
    breadcrumbsContext,
    "breadcrumbReplacementsReceived",
    (newReplacements) => {
      // combine replacements
      const newReplacementsComputed = BreadcrumbHelper.combineReplacements(
        replacementsComputedRef.current,
        newReplacements,
      );
      const isReplacementsChanged = !ComparisonHelper.isDeepEqual(
        newReplacements,
        replacementsComputedRef.current,
      );

      if (isReplacementsChanged) {
        replacementsComputedRef.current = newReplacementsComputed;
        idValueReplacementsMapRef.current = BreadcrumbHelper.combineReplacementsMap(
          idValueReplacementsMapRef.current,
          newReplacements,
        );
      }

      if (isReplacementsChanged) {
        applyIdValueReplacements(breadcrumbsComputedRef.current, idValueReplacementsMapRef.current);
      }
    },
  );

  if (breadcrumbsComputedRef.current.length < 2) {
    return null;
  }

  return (
    <Breadcrumbs
      data-testid='app-breadcrumbs'
      sx={{
        ...sx,
      }}
      maxItems={isMobile ? 2 : undefined}
      separator={<NavigateNextIcon fontSize='small' />}
      aria-label='breadcrumbs'
    >
      {breadcrumbsComputedRef.current.map((breadcrumb, index) => {
        const isLast = index === breadcrumbsComputedRef.current.length - 1;
        return (
          // for id value breadcrumbs render skeleton and wait for some time if title provided
          <DelayedRender
            key={index}
            enabled={(breadcrumb.isReplacementNeeded && !breadcrumb.isReplacementApplied) || false}
            delayMs={
              replacementsComputedRef.current?.isWaitInfinitely
                ? -1
                : replacementsComputedRef.current?.waitTimeout || 0
            }
            interruptIf={
              (breadcrumb.isReplacementNeeded && breadcrumb.isReplacementApplied) || false
            }
            isReScheduleOnDelayChange
            placeholder={<Skeleton variant='text' width={50} />}
          >
            <Typography
              data-testid='app-breadcrumb'
              component={AppLink}
              to={breadcrumb.to}
              variant='body1'
              disableLinkStyles={!isLast}
              sx={{ textDecoration: "none" }}
            >
              {breadcrumb.title}
            </Typography>
          </DelayedRender>
        );
      })}
    </Breadcrumbs>
  );
}
